<template>
  <div>
    <personel-list-add-new
      :is-add-new-user-sidebar-active.sync="isAddNewUserSidebarActive"
      :role-options="roleOptions"
      :plan-options="PLAN_OPTIONS"
      :gender-options="genderOptions"
      @refetch-data="refetchData"
      @add-user="addUser"
    />

    <print-list
      :list="printList"
      :is-print-list-sidebar-active.sync="isPrintListSidebarActive"
      @remove="removeFromPrintList"
      @exportToCsv="exportToCsv"
    />

    <!-- Filters -->
    <personel-list-filters
      v-if="appConfig.type === 'hw-admin'"
      :role-filter.sync="roleFilter"
      :plan-filter.sync="planFilter"
      :status-filter.sync="statusFilter"
      :state-filter.sync="stateFilter"
      :city-filter.sync="cityFilter"
      :age-filter.sync="ageFilter"
      :subcontractor-filter.sync="subcontractorFilter"
      :residency-filter.sync="residencyFilter"
      :role-options="roleOptions"
      :plan-options="PLAN_OPTIONS"
      :status-options="STATUS_OPTIONS"
      :state-options="STATE_OPTIONS"
      :age-options="AGE_OPTIONS"
      :subcontractor-options="SUBCONTRACTOR_OPTIONS"
      :residency-options="RESIDENCY_OPTIONS"
    />

    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Search -->
          <b-col>
            <div style="display: flex; flex-direction: row; justify-content: space-between; width: 100%; align-items: center;">
              <div style="display: flex; flex-direction: row; width: 50%;">
                <b-form-input
                  v-model="searchQuery"
                  
                  :placeholder="$t('application.addUserModal.placeholders.search')"
                  :debounce="inputDebounce"
                />
                <b-button
                  v-if="appConfig.type === 'ow-admin'"
                  style="margin-left: 20px;"
                  :variant="COLOR_VARIANTS.PRIMARY"
                  @click="isAddNewUserSidebarActive = true"
                >
                  <span class="text-nowrap">Lisää työntekijä</span>
                </b-button>
                <input id="fileInput" type="file" style="display: none;" @change="importUsers" />
                <b-button
                  v-if="appConfig.type === 'ow-admin'"
                  style="margin-left: 20px; display: flex; flex-direction: row; align-items: center;"
                  variant="secondary"
                  @click="openFileExplorer"
                >
                  <span class="text-nowrap">Tuo työntekijöitä</span>
                  <b-spinner v-if="isImporting" style="margin-left: 10px; width: 15px; height: 15px;" />
                </b-button>
                <b-button
                  v-if="appConfig.type === 'hw-admin'"
                  :variant="COLOR_VARIANTS.SECONDARY"
                  @click="() => isPrintListSidebarActive = true"
                >
                  <span v-if="printList.length < 1" class="text-nowrap">{{ $t('application.btn.create_list') }}</span>
                  <!-- show badge with print list count -->
                  <b-badge
                    v-else
                    variant="light"
                    class="text-nowrap"
                    style="cursor: pointer; color:black"
                    @click="() => isPrintListSidebarActive = true"
                  >
                    Listan koko: {{ printList.length }}
                  </b-badge>
                </b-button>
              </div>
              <div v-if="appConfig.type === 'ow-admin'" style="display: flex; flex-direction: column; align-items: center;">
                <h5>Aktiiviset työntekijät</h5>
                <b v-if="activeUsersThisMonth">{{ activeUsersThisMonth }}</b>
                <b-spinner v-else style="width: 20px; height: 20px;" />
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refUserListTable"
        class="position-relative"
        :hover="isPrintListSidebarActive"
        :items="fetchUsers"
        responsive
        :fields="appConfig.type === 'hw-admin' ? hoiwaColumns : oiwaColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_matching_records')"
        :sort-desc.sync="isSortDirDesc"
        @row-clicked="rowClicked"
      >
        <!-- Column: User -->
        <template #cell(user)="data">
          <b-media v-if="!isPrintListSidebarActive && printList.length < 1"><!-- TODO how to apply css class to enable vertical-aligh: top here ?-->
            <div style="display: flex; flex-direction: row; align-items: center;">
              <b-avatar
                size="32"
                style="margin-right: 10px;"
                :src="data.item.avatar"
                :text="avatarText(data.item.fullName)"
                :variant="`light-${resolveUserRoleVariant(data.item.role)}`"
                :to="{ name: 'apps-personels-view', params: { id: data.item.id } }"
              />
              <b-link
                :to="{ name: 'apps-personels-view', params: { id: data.item.id } }"
                class="font-weight-bold d-block text-nowrap"
                style="text-transform: capitalize"
              >
                {{ data.item.fullName }}
              </b-link>
            </div>
          </b-media>
          <b-media v-else><!-- TODO how to apply css class to enable vertical-aligh: top here ?-->
            <template #aside>
              <b-avatar
                size="32"
                :src="data.item.avatar"
                :text="avatarText(data.item.fullName)"
                :variant="`light-${resolveUserRoleVariant(data.item.role)}`"
              />
            </template>
            <b-link
              class="font-weight-bold d-block text-nowrap"
              style="text-transform: capitalize"
              @click="rowClicked(data.item)"
            >
              {{ data.item.fullName }}
            </b-link>
          </b-media>
        </template>

        <!-- Column: TempContract -->
        <template #cell(tempContract)="{ item }">
          <b-badge
            v-if="
              item.tempContract &&
                item.tempContract.some(item => tempContractIsActive(item.startDate, item.endDate))
            "
            pill
            variant="light-success"
            class="text-capitalize"
          >
            <feather-icon icon="CheckCircleIcon" size="18" class="mr-50" />
            {{ $t('personel.badge.temporaryWorker') }}
          </b-badge>
          <b-badge
            v-if="
              !item.tempContract ||
                item.tempContract.every(item => !tempContractIsActive(item.startDate, item.endDate))
            "
            pill
            variant="light-secondary"
            class="text-capitalize"
          >
            <feather-icon icon="UserIcon" size="18" class="mr-50" />
            {{ $t('personel.badge.gigWorker') }}
          </b-badge>
          <div
            v-if="
              item.tempContract &&
                item.tempContract.some(item => tempContractWillBeActive(item.startDate))
            "
          >
            <b-badge
              v-for="(contract, index) in item.tempContract.filter(item =>
                tempContractWillBeActive(item.startDate)
              )"
              :key="index"
              pill
              variant="light-info"
              class="text-capitalize"
            >
              <feather-icon icon="LoaderIcon" size="18" class="mr-50" />
              {{ contract.startDate.toDate().toLocaleDateString('fi') }} -
              {{ contract.endDate.toDate().toLocaleDateString('fi') }}
            </b-badge>
          </div>
          <div
            v-if="
              item.tempContract &&
                item.tempContract.some(item => tempContractWasActive(item.endDate))
            "
          >
            <b-badge
              v-for="(contract, index) in item.tempContract.filter(item =>
                tempContractWasActive(item.endDate)
              )"
              :key="index"
              pill
              variant="light-warning"
              class="text-capitalize"
            >
              <feather-icon icon="ClockIcon" size="18" class="mr-50" />
              {{ contract.startDate.toDate().toLocaleDateString('fi') }} -
              {{ contract.endDate.toDate().toLocaleDateString('fi') }}
            </b-badge>
          </div>
        </template>

        <!-- Column: Role -->
        <template #cell(role)="data">
          <div class="text-nowrap">
            <feather-icon
              :icon="resolveUserRoleIcon(data.item.role)"
              size="18"
              class="mr-50"
              :class="`text-${resolveUserRoleVariant(data.item.role)}`"
            />
            <span class="align-text-top text-capitalize">{{ data.item.role }}</span>
          </div>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge
            pill
            :variant="`light-${resolveUserStatusVariant(data.item.status)}`"
            class="text-capitalize"
          >
            {{ data.item.status }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret :right="$store.state.hwUiLayout.isRTL">
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
            </template>
            <b-dropdown-item :to="{ name: 'apps-personels-edit', params: { id: data.item.id } }">
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('edit') }}</span>
            </b-dropdown-item>

            <b-dropdown-item v-b-modal.modal-danger @click="deleteUser(data.item.id)">
              <feather-icon icon="TrashIcon" />
              <span class="align-middle ml-50">{{ $t('delete') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="12"
            class="d-flex align-items-center justify-content-center justify-content-sm-center"
          >
            <b-button
              v-show="canLoadMore"
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              pill
              :disabled="loadMore"
              variant="outline-secondary"
              @click="onLoadMore"
            >
              <div v-if="loadMore">
                <b-spinner small />
                Loading...
              </div>
              <div v-if="!loadMore">
                Load More
              </div>
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-card>
    <b-modal
      id="modal-danger"
      ok-only
      ok-variant="danger"
      ok-title="Delete"
      modal-class="modal-danger"
      centered
      title="Deleting User"
      @ok="confirmDelete"
    >
      <b-card-text>
        You are deleting a user, this operation cannot be done.
      </b-card-text>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardText,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BModal,
  VBModal,
  BButton,
  BSpinner,
} from 'bootstrap-vue'

import * as moment from 'moment'

import { ref } from '@vue/composition-api'
// eslint-disable-next-line import/no-cycle
import { avatarText } from '@hw-ui/utils/filter'
import Ripple from 'vue-ripple-directive'
import { db } from '@/shared/services/firebase'
// eslint-disable-next-line import/no-unresolved
import { mapGetters } from 'vuex'
import { COLOR_VARIANTS, INPUT_DEBOUNCE, STATE_OPTIONS, SUBCONTRACTOR_OPTIONS, STATUS_OPTIONS, PLAN_OPTIONS, AGE_OPTIONS, RESIDENCY_OPTIONS } from '@/shared/constants'
import PersonelListFilters from './PersonelListFilters.vue'
import usePersonelList from './usePersonelList'
import PersonelListAddNew from './PersonelListAddNew.vue'
import PrintList from '../../applications/applications-list/PrintList.vue'

export default {
  components: {
    PersonelListFilters,
    PersonelListAddNew,
    BModal,
    BCard,
    BCardText,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BButton,
    BSpinner,
    PrintList,
  },
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  data() {
    return {
      inputDebounce: INPUT_DEBOUNCE,
      COLOR_VARIANTS,
      STATE_OPTIONS,
      SUBCONTRACTOR_OPTIONS,
      PLAN_OPTIONS,
      AGE_OPTIONS,
      RESIDENCY_OPTIONS,
      STATUS_OPTIONS,
      printList: [],
      activeUsersThisMonth: null,
      importFile: null,
      isImporting: false,
    }
  },
  computed: {
    ...mapGetters('appAccount', ['roleOptions']),
    appConfig() {
      return this.$store.state.app.appConfig || {}
    },
  },
  watch: {
    importFile() {
      this.isImporting = this.importFile
    }
  },
  async mounted() {
    if (this.appConfig.type === 'ow-admin') {
      const now = new Date()
      const thisMonth = (now.getMonth() + 1) < 9
        ? '0'.concat(now.getMonth() + 1)
        : now.getMonth() + 1

      const shiftsCollection = await db.collection('shifts').where('start', '>=', new Date(1, (now.getMonth() + 1), now.getFullYear(), 0, 0, 0)).get()
      const users = shiftsCollection
        .docs
        .filter(shift => shift.data().startStr.includes(`${now.getFullYear()}-${thisMonth}`) && shift.data().assignedTo !== '')
        .map(shift => shift.data().assignedTo)

      const uniqueUsers = users.filter((item, index) => users.indexOf(item) === index)
      
      this.activeUsersThisMonth = uniqueUsers.length
    }
  },
  methods: {
    openFileExplorer() {
      document.getElementById('fileInput').click()
    },
    importUsers(e) {
      // eslint-disable-next-line prefer-destructuring
      this.importFile = e.target.files[0]

      try {
        if (e.target.files[0].type !== 'text/csv') {
          return
        }
        // eslint-disable-next-line prefer-destructuring
        this.importFile = e.target.files[0]
        
        const reader = new FileReader()
        reader.onload = async event => {
          const data = event.target.result
            .replaceAll('\x85', 'Ö')
            .replaceAll('\x9A', 'ö')
            .replaceAll('\x80', 'Ä')
            .replaceAll('\x8A', 'ä')
            .split('\n').filter((item, index) => item !== "" && index > 0)
            .map(item => item.split(';'))
          const users = data.map(item => ({
            firstName: item[0].toLowerCase(),
            lastName: item[1].toLowerCase(),
            fullName: `${item[0].toLowerCase()} ${item[1].toLowerCase()}`,
            email: item[2].toLowerCase(),
            mobile: item[3],
            birthday: item[4],
            address: {
              streetAddress: item[5].toLowerCase(),
              city: item[6].toLowerCase(),
              postcode: item[7],
              state: item[8].toLowerCase(),
            },
            profession: item[9].toLowerCase(),
            role: item[9].toLowerCase(),
            finnish: item[10].toLowerCase(),
            drivingLicense: item[11].split(',').map(type => type.trim().toLowerCase()),
            languages: item[12].split(',').map(language => language.trim().toLowerCase()),
            carAvailable: ["TRUE", "true", "True", "Kyllä", "kyllä", "yes", "Yes", "y", "Y"].includes(item[13]),
            confirmed: true,
            files: [],
            isAdmin: false,
            residency: 'finland',
            status: 'accepted',
            created: new Date(),
          }))

          // eslint-disable-next-line no-return-assign
          await this.addUsersFromCsv(users)
          this.importFile = null
        }
        reader.readAsText(this.importFile, 'ISO-8859-4')
      } catch (err) {
        this.importFile = null
        console.error(err)
      }
    },
    onLoadMore() {
      this.currentPage += 1
      this.loadMore = true
    },
    removeFromPrintList(id) {
      const index = this.printList.findIndex(item => item.id === id)
      this.printList.splice(index, 1)
    },
    rowClicked(item) {
      if(!this.isPrintListSidebarActive && this.printList.length === 0) {
        return
      }
      // Check if is already in list, if not add object to list
      const index = this.printList.findIndex(printItem => printItem.id === item.id)
      if(index === -1) {
        this.printList.push(item)
        this.$bvToast.toast(`Added ${item.fullName} to print list`, {
          title: 'Success',
          variant: 'info',
          solid: false,
        })
      }
    },
    async exportToCsv() {
      /** Export data to csv */
      const data = this.printList.map(item => {
        const { fullName, email, mobile, role, love, createdAt} = item
        return {
          applied: moment(createdAt).format('MM.DD.YYYY'),
          name: fullName,
          rooli: role,
          luvallinen: love ? 'Kyllä' : 'Ei',
          puhelin: mobile,
          soitettu: ' ',
          muistiinpanot: ' ',
          sitoutuuko: ' ',
          email,
          link: `https://hoiwa-app.web.app/apps/hr/applications/view/${item.id}`,
        }
      })
      const replacer = (key, value) => (value === null ? '' : value) // specify how you want to handle null values here
      const header = Object.keys(data[0])
      let csv = data.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','))
      csv.unshift(header.join(','))
      csv = csv.join('\r\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', 'puhelu_lista_tyontekijat.csv')
      link.click()
    },
  },
  setup() {

    const isAddNewUserSidebarActive = ref(false)
    const isPrintListSidebarActive = ref(false)

    const genderOptions = [
      { label: 'Female', value: 'female' },
      { label: 'Male', value: 'male' },
    ]

    const {
      fetchUsers,
      deleteUser,
      confirmDelete,
      hoiwaColumns,
      oiwaColumns,
      perPage,
      currentPage,
      totalUsers,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,
      addUser,
      addUsersFromCsv,
      loadMore,
      canLoadMore,

      // UI
      resolveUserRoleVariant,
      resolveUserRoleIcon,
      resolveUserStatusVariant,

      // Extra Filters
      roleFilter,
      planFilter,
      stateFilter,
      cityFilter,
      statusFilter,
      ageFilter,
      subcontractorFilter,
      residencyFilter,

      tempContractIsActive,
      tempContractWasActive,
      tempContractWillBeActive,
    } = usePersonelList()

    return {
      // Sidebar
      isAddNewUserSidebarActive,
      isPrintListSidebarActive,
      fetchUsers,
      deleteUser,
      confirmDelete,
      hoiwaColumns,
      oiwaColumns,
      perPage,
      currentPage,
      totalUsers,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,
      addUser,
      addUsersFromCsv,
      loadMore,
      canLoadMore,
      genderOptions,

      // Filter
      avatarText,

      // UI
      resolveUserRoleVariant,
      resolveUserRoleIcon,
      resolveUserStatusVariant,

      // Extra Filters
      roleFilter,
      planFilter,
      stateFilter,
      cityFilter,
      statusFilter,
      ageFilter,
      subcontractorFilter,
      residencyFilter,

      tempContractIsActive,
      tempContractWasActive,
      tempContractWillBeActive,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@hw-ui/scss/vue/libs/vue-select.scss';
</style>
